import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconView } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';

interface ScheduleRow {
  id: number;
  employer_id: number;
  employer: { id: number; name: string };
  period_start: string;
  period_end: string;
  status: 'draft' | 'submitted' | 'receipted';
  total_due: number;
  outstanding: number;
}

interface EmployerOption {
  id: number;
  name: string;
}

const STATUS_BADGE: Record<ScheduleRow['status'], string> = {
  draft: 'badge-scheduled',
  submitted: 'badge-neutral',
  receipted: 'badge-active',
};

function formatPeriod(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  const s = new Date(start).toLocaleDateString('en-GB', opts);
  const e = new Date(end).toLocaleDateString('en-GB', { ...opts, year: 'numeric' });
  return `${s} – ${e}`;
}

const NO_FILTERS = {};

const emptyForm = { employer_id: '', period_start: '', period_end: '' };

export function ContributionSchedules() {
  const { can, user } = useAuth();
  const { settings } = useConfig();
  const navigate = useNavigate();

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [employers, setEmployers] = useState<EmployerOption[]>([]);

  // A self-service employer user has no employer.contributions view of the
  // full list — their own employer is implicit server-side, so they never
  // pick one when creating a schedule.
  const isSelfServiceEmployer = Boolean(user?.employer_id);
  const canCreate = can('memberships.contributions.manage') || can('employer.contributions.manage');

  const { data, loading, error, setPage } = usePaginatedList<ScheduleRow>('/contribution-schedules', NO_FILTERS);

  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  useEffect(() => {
    if (isSelfServiceEmployer) return;
    api
      .get<{ data: EmployerOption[] }>('/employers', { params: { per_page: 100 } })
      .then((res) => setEmployers(res.data.data))
      .catch(() => setEmployers([]));
  }, [isSelfServiceEmployer]);

  const openAdd = () => {
    setForm(emptyForm);
    setErrors({});
    setAdding(true);
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const payload: Record<string, unknown> = { period_start: form.period_start, period_end: form.period_end };
      if (!isSelfServiceEmployer) payload.employer_id = form.employer_id;
      const res = await api.post<{ id: number }>('/contribution-schedules', payload);
      setAdding(false);
      navigate(`/memberships/contribution-schedules/${res.data.id}`);
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Contribution Schedules</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Add
          </button>
        )}
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Employer</th>
                <th>Period</th>
                <th>Status</th>
                <th>Total Due</th>
                <th>Outstanding</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((schedule) => (
                  <tr key={schedule.id}>
                    <td>
                      <strong>{schedule.employer.name}</strong>
                    </td>
                    <td>{formatPeriod(schedule.period_start, schedule.period_end)}</td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[schedule.status]}`}>{schedule.status}</span>
                    </td>
                    <td>{fmtMoney(schedule.total_due)}</td>
                    <td>{fmtMoney(schedule.outstanding)}</td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/memberships/contribution-schedules/${schedule.id}`} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No contribution schedules yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="New contribution schedule" onClose={() => setAdding(false)}>
          <form onSubmit={submitAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            {!isSelfServiceEmployer && (
              <label className="span-2">
                Employer *
                <select
                  value={form.employer_id}
                  onChange={(e) => setForm({ ...form, employer_id: e.target.value })}
                  required
                >
                  <option value="">Select…</option>
                  {employers.map((employer) => (
                    <option key={employer.id} value={employer.id}>
                      {employer.name}
                    </option>
                  ))}
                </select>
                {errors.employer_id && <small className="error">{errors.employer_id[0]}</small>}
              </label>
            )}
            <label>
              Period start *
              <input
                type="date"
                value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                required
              />
              {errors.period_start && <small className="error">{errors.period_start[0]}</small>}
            </label>
            <label>
              Period end *
              <input
                type="date"
                value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                required
              />
              {errors.period_end && <small className="error">{errors.period_end[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!form.period_start || !form.period_end}>
                Create
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
